/* eslint-disable no-unused-vars */
import styled, { css } from 'styled-components';
import { Palette } from 'config';

const Base = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: fit-content;
  height: fit-content;
  font-size: 14px;
  line-height: 24px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid transparent;
  user-select: none;
  transition: all 0.1s ease-in-out;
  svg,
  svg > path {
    transition: all 0.1s ease-in-out;
  }

  :hover {
    transition: all 0.1s ease-in-out;
  }

  :disabled {
    cursor: not-allowed;
  }
`;

const Primary = styled(Base)`
  color: ${Palette.White._000};
  box-shadow: 0px 2px 2px rgba(28, 53, 106, 0.05);
  background-color: ${Palette.Primary._500};

  svg,
  svg > path {
    fill: currentColor;
  }

  :hover {
    background-color: ${Palette.Primary._600};
  }

  :disabled {
    opacity: 0.3;
  }

  :active {
    color: ${Palette.Primary._100};
    box-shadow: 0px 0px 0px 3px rgba(59, 137, 246, 0.16);
    background-color: ${Palette.Primary._500};

    svg,
    svg > path {
      fill: currentColor;
    }
  }
`;

const Secondary = styled(Base)`
  color: ${Palette.Primary._500};
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.05);
  background-color: ${Palette.White._000};
  border: 1px solid ${Palette.PrimaryGray._200};

  svg,
  svg > path {
    fill: currentColor;
  }

  :hover {
    border: 1px solid ${Palette.Primary._300};
  }

  :active {
    color: ${Palette.Primary._400};
    box-shadow: 0px 0px 0px 3px rgba(59, 137, 246, 0.16);
    background-color: ${Palette.Primary._000};

    svg,
    svg > path {
      fill: currentColor;
    }
  }

  :disabled {
    opacity: 0.5;
  }

  ${({ isPressed }) =>
    isPressed &&
    css`
      background-color: ${Palette.Primary._000};
      border: 1px solid ${Palette.Primary._300};
      box-shadow: unset;
    `}
`;

const Tertiary = styled(Base)`
  color: ${Palette.PrimaryGray._700};
  background-color: transparent;

  svg,
  svg > path {
    fill: ${Palette.PrimaryGray._500};
  }

  :hover {
    color: ${Palette.PrimaryGray._900};
    background-color: ${Palette.PrimaryGray._100};
    border: 1px solid transparent;

    svg,
    svg > path {
      fill: ${Palette.Primary._500};
    }
  }

  :disabled {
    color: ${Palette.PrimaryGray._800};
    opacity: 0.5;

    svg,
    svg > path {
      fill: ${Palette.PrimaryGray._500};
    }
  }

  :active {
    color: ${Palette.PrimaryGray._500};
    box-shadow: 0px 0px 0px 3px rgba(59, 137, 246, 0.16);
    background-color: ${Palette.White._000};

    svg,
    svg > path {
      fill: ${Palette.Primary._400};
    }
  }

  ${({ isPressed }) =>
    isPressed &&
    css`
      color: ${Palette.Primary._500};
      background-color: ${Palette.Primary._000};
      border: 1px solid ${Palette.Primary._300};
    `}

  ${({ isMuted }) =>
    isMuted &&
    css`
      color: ${Palette.PrimaryGray._700};

      svg,
      svg > path {
        fill: ${Palette.PrimaryGray._400};
      }
    `}

  ${({ isDestructive }) =>
    isDestructive &&
    css`
      :hover {
        color: ${Palette.Red._500};
        border: 1px solid transparent;

        svg,
        svg > path {
          fill: ${Palette.Red._500};
        }
      }
    `}
`;

const Quaternary = styled(Base)`
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: ${Palette.PrimaryGray._500};
  background-color: transparent;

  svg,
  svg > path {
    fill: currentColor;
  }

  :hover {
    background-color: ${Palette.PrimaryGray._100};
  }

  :active {
    background-color: ${Palette.White._000};
    box-shadow: 0px 0px 0px 3px rgba(59, 137, 246, 0.16);
    border: 1px solid transparent;
  }

  :disabled {
    color: ${Palette.PrimaryGray._500};
    opacity: 0.5;

    svg,
    svg > path {
      fill: currentColor;
    }
  }

  ${({ isPressed }) =>
    isPressed &&
    css`
      background-color: ${Palette.Primary._000};
      border: 1px solid ${Palette.Primary._300};
    `}

  ${({ isMuted }) =>
    isMuted &&
    css`
      color: ${Palette.PrimaryGray._700};

      svg,
      svg > path {
        fill: ${Palette.PrimaryGray._400};
      }
    `}
`;

const Destructive = styled(Base)`
  color: ${Palette.White._000};
  background-color: ${Palette.Red._500};

  svg,
  svg > path {
    fill: currentColor;
  }

  :hover {
    background-color: ${Palette.Red._600};
  }

  :disabled {
    opacity: 0.3;
  }

  :active {
    color: ${Palette.Red._100};
    box-shadow: 0px 0px 0px 3px rgba(222, 48, 18, 0.1);
    background-color: ${Palette.Red._600};
  }
`;

export const PrimaryNormal = styled(Primary)`
  padding: 7px 24px;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const PrimarySmall = styled(Primary)`
  padding: 3px 16px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SecondaryNormal = styled(Secondary)`
  padding: 7px 24px;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const SecondarySmall = styled(Secondary)`
  padding: 3px 16px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const TertiaryNormal = styled(Tertiary)`
  padding: 7px 8px;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const TertiarySmall = styled(Tertiary)`
  padding: 3px 8px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const QuaternaryNormal = styled(Quaternary)`
  padding: 7px 7px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const DestructiveNormal = styled(Destructive)`
  padding: 7px 24px;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const DestructiveSmall = styled(Destructive)`
  padding: 3px 16px;

  svg {
    width: 16px;
    height: 16px;
  }
`;
